import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu as MenuIcon, ShoppingBag, X, Instagram, Phone } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import CartDrawer from "./CartDrawer";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Breakfast By Sapore" },
  { to: "/events", label: "Events & Catering" },
  { to: "/other-menus", label: "Other Menus" },
  { to: "/contact", label: "Contact" },
];

export function AnnouncementBanner() {
  const { settings } = useSettings();
  if (!settings?.banner_active || !settings?.banner_text) return null;
  return (
    <div
      data-testid="announcement-banner"
      className="bg-sapore-purple text-sapore-cream py-2 px-4 text-center text-xs sm:text-sm font-medium border-b border-sapore-gold/30"
    >
      {settings.banner_text}
    </div>
  );
}

export function FloatingWhatsApp() {
  const { settings } = useSettings();
  const number = settings?.whatsapp || "2348094706281";
  return (
    <a
      data-testid="floating-whatsapp-button"
      href={`https://wa.me/${number}`}
      target="_blank"
      rel="noreferrer"
      className="fixed bottom-6 right-6 z-40 bg-sapore-whatsapp hover:bg-sapore-whatsappDark text-white p-4 rounded-full shadow-2xl hover:scale-105 transition-transform flex items-center justify-center"
      aria-label="Chat on WhatsApp"
    >
      <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
        <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.523 5.26l-.999 3.648 3.965-1.04zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.767.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
      </svg>
    </a>
  );
}

export default function SiteLayout({ children }) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { count, setOpen } = useCart();
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-sapore-cream">
      <AnnouncementBanner />
      <header className="sticky top-0 z-40 bg-sapore-cream/90 backdrop-blur-md border-b border-sapore-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="nav-logo-link" className="flex items-center">
              <img
                src="/assets/sapore-logo-purple.png"
                alt="Sapore Eats"
                data-testid="brand-logo"
                className="h-10 sm:h-12 w-auto rounded-md"
              />
            </Link>
            <nav className="hidden lg:flex items-center gap-8">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-link-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`text-sm font-medium tracking-wide transition-colors hover:text-sapore-purple ${
                    pathname === n.to ? "text-sapore-purple" : "text-sapore-inkSoft"
                  }`}
                >
                  {n.label}
                </Link>
              ))}
            </nav>
            <div className="flex items-center gap-2">
              <button
                data-testid="cart-drawer-toggle"
                onClick={() => setOpen(true)}
                className="relative p-2.5 rounded-full bg-sapore-purple text-sapore-cream hover:bg-sapore-purpleDark transition-colors"
                aria-label="Open cart"
              >
                <ShoppingBag size={20} />
                {count > 0 && (
                  <span
                    data-testid="cart-count-badge"
                    className="absolute -top-1 -right-1 bg-sapore-gold text-sapore-ink text-xs font-bold h-5 min-w-5 px-1 rounded-full flex items-center justify-center"
                  >
                    {count}
                  </span>
                )}
              </button>
              <button
                className="lg:hidden p-2.5 rounded-full border border-sapore-border text-sapore-purple"
                onClick={() => setMobileOpen((v) => !v)}
                data-testid="mobile-menu-toggle"
                aria-label="Menu"
              >
                {mobileOpen ? <X size={20} /> : <MenuIcon size={20} />}
              </button>
            </div>
          </div>
        </div>
        {mobileOpen && (
          <nav className="lg:hidden border-t border-sapore-border bg-sapore-cream px-4 py-3 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="py-2.5 px-2 rounded-lg text-sapore-ink font-medium hover:bg-sapore-goldSubtle"
              >
                {n.label}
              </Link>
            ))}
          </nav>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <Footer />
      <FloatingWhatsApp />
      <CartDrawer />
    </div>
  );
}

function Footer() {
  const { settings } = useSettings();
  return (
    <footer className="bg-sapore-purple text-sapore-cream mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <img src="/assets/sapore-logo-transparent.png" alt="Sapore Eats" className="h-14 w-auto mb-3" />
          <p className="text-xs uppercase tracking-[0.3em] text-sapore-gold mb-4">Breakfast &amp; Catering</p>
          <p className="text-sapore-cream/80 max-w-sm text-sm leading-relaxed">
            Artisanal Nigerian breakfast and cold-pressed juices, delivered to your door. We also cater private and
            corporate events.
          </p>
        </div>
        <div>
          <h4 className="font-serif text-lg mb-3 text-sapore-gold">Explore</h4>
          <ul className="space-y-2 text-sm text-sapore-cream/80">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="hover:text-sapore-gold transition-colors">
                  {n.label}
                </Link>
              </li>
            ))}
            <li><Link to="/policy/terms" className="hover:text-sapore-gold transition-colors">Terms</Link></li>
            <li><Link to="/policy/privacy" className="hover:text-sapore-gold transition-colors">Privacy</Link></li>
            <li><Link to="/policy/refund" className="hover:text-sapore-gold transition-colors">Refund Policy</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-serif text-lg mb-3 text-sapore-gold">Reach Us</h4>
          <ul className="space-y-3 text-sm text-sapore-cream/80">
            <li className="flex items-center gap-2">
              <Phone size={15} /> <a href={`tel:${settings?.phone || ""}`} className="hover:text-sapore-gold">{settings?.phone}</a>
            </li>
            <li className="flex items-center gap-2">
              <Instagram size={15} />
              <a href={settings?.instagram || "#"} target="_blank" rel="noreferrer" className="hover:text-sapore-gold">
                @sapore.eats
              </a>
            </li>
            <li>{settings?.business_hours}</li>
            <li className="text-sapore-cream/60">Delivery only — no walk-in address.</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-sapore-cream/60">
        © {new Date().getFullYear()} Sapore Eats · Est. 2025 · Made with care in Nigeria
      </div>
    </footer>
  );
}
